import React from 'react';

import { Alert, Box, Button, Divider, Drawer, Stack, Typography } from '@mui/material';

type Props = { open: boolean; onClose: () => void };

export default function ProductPerformanceMethod({ open, onClose }: Props) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 510 }, maxWidth: '100%' } } }}
    >
      <Stack gap={2} sx={{ p: 3, overflow: 'auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography component="h2" variant="h5">
            Method and sources
          </Typography>
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        </Stack>
        <Alert severity="info">
          Live. Every section queries lux-analytics-prod.reporting.sales when the page loads or a filter changes.
          reporting.sales is rebuilt daily, so recent days can move as orders are cancelled or adjusted.
        </Alert>
        <Box>
          <Typography component="h3" variant="subtitle1">
            Source and scope
          </Typography>
          <Typography variant="body2">
            One row per reporting.sales row, the grain of the Tableau Sales Model behind the Product Performance
            workbook. Cancelled or missing orders, rows without a brand or transaction type, and an empty vendor parent
            are excluded. Item status defaults to completed and awaiting dates. The query definition lives in
            svc-reporting (native-specs/product-performance.json) and changes only through code review.
          </Typography>
        </Box>
        <Divider />
        <Box>
          <Typography component="h3" variant="subtitle1">
            Measures and dates
          </Typography>
          <Typography variant="body2">
            Sales AUD sums gross_sales_aud. Margin AUD sums margin_adjusted_aud. Adjusted margin % divides those period
            sums. Total margin adds each row’s estimated margin override where both are known; its rate divides by
            sales.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Packages and room nights sum the source measures. Orders count distinct orders. Vendor ADR averages the
            row-level cost-sales AUD ÷ room nights. Lead time averages check-in minus transaction time. Average LOS
            divides total nights by total packages. Adults, children and infants are separate measures.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Dates are Sydney transaction dates. Product and Margin Performance follow the date range. Low Margin always
            covers the last 7 days and Weekly Trends the last 8 complete Monday weeks. Low Margin applies its range to
            each vendor, booking and transaction-time group’s adjusted margin.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Period Comparisons covers the current month to yesterday and the three months before it, excluding an
            unknown order currency. Destination and currency compare each month with the previous month; product
            compares each product with the one above it in the same month, as saved in the workbook.
          </Typography>
        </Box>
        <Box>
          <Typography component="h3" variant="subtitle1">
            Filters and drill-through
          </Typography>
          <Typography variant="body2">
            Filters apply before any totals are calculated. Select a destination, product, opportunity, currency or
            brand name in a table to filter every section; remove its chip to clear it. Margin Performance covers the
            four hotel products in the report’s 25 margin currencies; Weekly Trends covers cruise, flight, hotel and
            tour products.
          </Typography>
        </Box>
        <Box>
          <Typography component="h3" variant="subtitle1">
            Missing values and limits
          </Typography>
          <Typography variant="body2">
            Sums skip missing values, as the Tableau calculations do. Unknown means no computable value; zero stays
            zero. Deal and vendor breakdown lists up to the 10,000 largest pairs by sales, every pair in a typical
            month. A section that reaches its row limit says so instead of showing a partial total.
          </Typography>
        </Box>
      </Stack>
    </Drawer>
  );
}
